import type { FieldDef } from '~/components/form/utils';
import { invoiceOptionLabel, type ScopedInvoiceOption } from '~/components/tickets/invoiceLinking';
import type { Car, PropertyForm } from '~/data/generated/model';
import type { ProblemTypeOption } from '~/data/hooks/useTicketProblemTypes';
import type { InvoiceFormOption } from '~/data/schemas/invoice';
import type { ServiceProviderListItem } from '~/data/schemas/serviceProvider';
import type { DisabledFields, EntryTypeKey } from '~/data/schemas/ticket';
import { entryTypeOptions, isOfCallBackType } from '~/data/schemas/ticket';
import { LocaleTerms } from '~/hooks/useLocaleTerms';

type CarForForm = Pick<Car, 'id' | 'name' | 'ahjId'>;

/**
 * Every TicketDto field the ticket form actually renders (union across entry
 * types). If `zodResolver(ticketDtoSchema)` blocks a submit on a field NOT in
 * this set, the user cannot see or fix it — Save silently does nothing. That was
 * the AMP-585 incident (`startedTimeHolidayName`). TicketForm's onInvalid path
 * uses this set to detect that case and report it to Sentry. Kept honest by the
 * drift test in `addTicketFields.test.ts`.
 */
export const RENDERED_TICKET_FIELD_NAMES: ReadonlySet<string> = new Set([
  'propertyId',
  'invoiceId',
  'serviceProviderGroup',
  'startedTime',
  'finishedTime',
  'callTime',
  'number',
  'entryType',
  'problemType',
  'problemDescription',
  'workPerformed',
  'carIds',
  'timeOnSiteRegularHours',
  'travelTimeRegularHours',
  'timeOnSiteOvertime',
  'travelTimeOvertime',
  'notes',
]);

interface TicketFieldDefOptions {
  properties?: PropertyForm[];
  ticketType: EntryTypeKey;
  cars?: CarForForm[];
  invoices?: Array<InvoiceFormOption | ScopedInvoiceOption>;
  disabledFields: DisabledFields;
  serviceProviderGroup: ServiceProviderListItem[];
  problemTypes?: ProblemTypeOption[];
  terms: LocaleTerms;
}

export const buildFieldDef = ({
  properties,
  cars,
  ticketType,
  serviceProviderGroup,
  invoices,
  disabledFields,
  problemTypes,
  terms,
}: TicketFieldDefOptions) => {
  const generalFields: FieldDef[] = [
    /** TODO: Ticket hidden properties:
     *  Date, Service Provider and Property Manager
     *  In the API integration issue, these values will be
     *  inferred as follows:
     * - Date -> Date.now()
     * - Service Provider -> From Car
     * - Property Manager -> From Property */
    {
      name: 'propertyId',
      label: 'Property',
      type: 'autocomplete',
      options:
        properties
          ?.filter((property) => property.id)
          .map((property) => ({
            id: property.id as string,
            label: property.name,
          })) ?? [],
      loading: properties === undefined,
      disabled: disabledFields['propertyId'] ?? false,
    },
    {
      name: 'invoiceId',
      label: 'Invoice #',
      type: 'autocomplete',
      options:
        invoices?.map((invoice) => ({
          id: invoice.id,
          label: invoiceOptionLabel(invoice),
        })) ?? [],
      loading: invoices === undefined,
      required: false,
      disabled: disabledFields['invoiceId'] ?? false,
    },
    {
      name: 'serviceProviderGroup',
      label: 'Service Provider ',
      type: 'autocomplete',
      required: true,
      gridProps: { xs: 12 },
      options:
        serviceProviderGroup?.map((provider) => ({
          id: provider.id,
          label: provider.name,
        })) ?? [],
      loading: serviceProviderGroup === undefined,
      disabled: disabledFields['serviceProviderGroup'] ?? false,
    },
  ];

  const maintenanceFields: FieldDef[] = [
    {
      name: 'startedTime',
      label: 'Arrival Time',
      type: 'datetime-local',
      disabled: disabledFields['startedTime'] ?? false,
    },
    {
      name: 'finishedTime',
      label: 'Departure Time',
      type: 'datetime-local',
      disabled: disabledFields['finishedTime'] ?? false,
    },
  ];

  const callBackFields: FieldDef[] = [
    {
      name: 'callTime',
      label: 'Call Time',
      type: 'datetime-local',
      required: false,
      disabled: disabledFields['callTime'] ?? false,
    },
    { name: 'callTime-spacer', type: 'spacer' },
    {
      name: 'startedTime',
      label: 'Response Time',
      type: 'datetime-local',
      disabled: disabledFields['startedTime'] ?? false,
    },
    {
      name: 'finishedTime',
      label: 'Resolution Time',
      type: 'datetime-local',
      disabled: disabledFields['startedTime'] ?? false,
    },
  ];

  const ticketFields: FieldDef[] = [
    {
      name: 'number',
      label: 'Ticket Number',
      type: 'text',
      disabled: disabledFields['number'] ?? false,
    },

    {
      name: 'entryType',
      label: 'Entry Type',
      type: 'select',
      options: entryTypeOptions.map(([value, label]) => ({ id: value, label })),
      disabled: disabledFields['entryType'] ?? false,
    },
    // only show problem description if ticketType is callBackType
    ...(isOfCallBackType(ticketType)
      ? [
          {
            name: 'problemType',
            label: 'Problem Type',
            type: 'select',
            gridProps: { xs: 12 },
            required: false,
            options: problemTypes?.map((pt) => ({ id: pt.name, label: pt.name })) ?? [],
            loading: problemTypes === undefined,
            disabled: disabledFields['problemType'] ?? false,
          } as FieldDef,
          {
            name: 'problemDescription',
            label: 'Problem Description',
            type: 'text',
            gridProps: { xs: 12 },
            disabled: disabledFields['problemDescription'] ?? false,
          } as FieldDef,
        ]
      : []),
    // if ticketType is callBackType, the show corrective action otherwise show work performed
    {
      name: 'workPerformed',
      label: isOfCallBackType(ticketType) ? 'Corrective Action' : 'Work Performed',
      type: 'text',
      gridProps: { xs: 12 },
      disabled: disabledFields['workPerformed'] ?? false,
    } as FieldDef,
    {
      name: 'carIds',
      label: terms.vehiclesServiced,
      type: 'autocomplete-multiple',
      gridProps: { xs: 12 },
      options: cars
        ? cars.map((car) => ({
            id: car.id,
            label: car.name + (car.ahjId ? ` - ${car.ahjId}` : ''),
          }))
        : [],
      loading: cars === undefined,
      disabled: disabledFields['carIds'] ?? false,
    },
    ...(isOfCallBackType(ticketType) ? callBackFields : maintenanceFields),
    {
      name: 'timeOnSiteRegularHours',
      label: 'Time on Site (Regular Hours)',
      type: 'time',
      required: false,
      disabled: disabledFields['timeOnSiteRegularHours'] ?? false,
    },
    {
      name: 'travelTimeRegularHours',
      label: 'Travel Time (Regular Hours)',
      type: 'time',
      required: false,
      disabled: disabledFields['travelTimeRegularHours'] ?? false,
    },
    {
      name: 'timeOnSiteOvertime',
      label: 'Time on Site (Overtime)',
      type: 'time',
      required: false,
      disabled: disabledFields['timeOnSiteOvertime'] ?? false,
    },
    {
      name: 'travelTimeOvertime',
      label: 'Travel Time (Overtime)',
      type: 'time',
      required: false,
      disabled: disabledFields['travelTimeOvertime'] ?? false,
    },
  ];

  const notesField: FieldDef = {
    name: 'notes',
    label: 'Notes',
    type: 'text',
    required: false,
    gridProps: { xs: 12 },
    disabled: disabledFields['notes'] ?? false,
  };

  return {
    generalFields,
    ticketFields,
    notesField,
  };
};
